import { createAction } from '@reduxjs/toolkit';
import { useState } from 'react';

import { useModal } from '@perpay-web/components/base/Modal/useModal';
import { useActionObservableCallback } from '@perpay-web/hooks/useActionObservableCallback';
import { hookAction$ } from '@perpay-web/services/singletons';
import { createContextProvider } from '@perpay-web/utils/componentUtils';

const setIsModalOpenAction = createAction(
    'CARD_APPLICATION_CONFIRM_ADDRESS_MODAL::SET_IS_MODAL_OPEN',
);

const setAptValidationRequiredAction = createAction(
    'CARD_APPLICATION_CONFIRM_ADDRESS_MODAL::SET_APT_VALIDATION_REQUIRED',
);

export const cardApplicationConfirmAddressModalOpen = () =>
    setIsModalOpenAction(true);

export const cardApplicationConfirmAddressModalClose = () =>
    setIsModalOpenAction(false);

export const cardApplicationConfirmAddressModalSetAptValidationRequiredFlag = (
    validationRequired,
) => setAptValidationRequiredAction(validationRequired);

/**
 * Hook for managing card application address confirmation modal state.
 *
 * Manages both modal visibility and apartment number validation requirement flag.
 * This hook is used internally by the context provider.
 *
 * @returns {Object} Modal state and setters
 * @returns {boolean} returns.isModalOpen - Whether modal is currently open
 * @returns {Function} returns.setIsModalOpen - Function to set modal open state
 * @returns {boolean} returns.aptNumberValidationRequired - Whether apt validation is required
 * @returns {Function} returns.setAptNumberValidationRequired - Function to set validation requirement
 */
const useCardApplicationConfirmAddressModal = () => {
    const { isModalOpen, setIsModalOpen } = useModal();
    const [aptNumberValidationRequired, setAptNumberValidationRequired] =
        useState(true);

    return {
        isModalOpen,
        setIsModalOpen,
        aptNumberValidationRequired,
        setAptNumberValidationRequired,
    };
};

const providerFactory = (Context) => {
    const Provider = ({ children }) => {
        const hook = useCardApplicationConfirmAddressModal();

        useActionObservableCallback(
            hookAction$,
            setIsModalOpenAction().type,
            (value) => hook.setIsModalOpen(value),
        );

        useActionObservableCallback(
            hookAction$,
            setAptValidationRequiredAction().type,
            (value) => hook.setAptNumberValidationRequired(value),
        );

        return <Context.Provider value={hook}>{children}</Context.Provider>;
    };

    return Provider;
};

export const [
    CardApplicationConfirmAddressModalProvider,
    useCardApplicationConfirmAddressModalContext,
] = createContextProvider(providerFactory);
